import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SERVICES, PHONE_DISPLAY, PHONE_TEL } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "", phone: "", email: "", vehicle_year: "", vehicle_make: "",
  vehicle_model: "", service_needed: "", preferred_date: "", message: "",
  referral_source: "",
};

const REFERRAL_OPTIONS = [
  "Google Search", "Facebook / Instagram", "Friend / Family Referral",
  "Repeat Customer", "Drove By / Saw Our Vehicle", "Other",
];

const validateForm = (form) => {
  const required = ["name", "phone", "vehicle_year", "vehicle_make", "vehicle_model", "service_needed"];
  return required.every((field) => form[field]);
};

const inputCls = "bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 h-11 rounded-sm focus-visible:ring-blue-600";

const SuccessCard = ({ firstName }) => (
  <div data-testid="form-success-message" className="bg-[#121212] border border-blue-500/40 rounded-xl p-10 text-center">
    <CheckCircle2 className="h-14 w-14 text-blue-500 mx-auto mb-5" />
    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">Request Received!</h2>
    <p className="text-zinc-400 leading-relaxed mb-8 max-w-md mx-auto">
      Thanks, {firstName}! Mason's will review your request and reach out shortly
      to confirm details and give you an upfront quote.
    </p>
    <p className="text-zinc-600 text-xs leading-relaxed mb-8 max-w-md mx-auto">
      By scheduling this appointment, you confirmed your acceptance of our{" "}
      <Link to="/terms" target="_blank" className="text-blue-400 underline underline-offset-2">
        Terms of Service and Repair Authorization
      </Link>.
    </p>
    <a
      data-testid="success-call-button"
      href={PHONE_TEL}
      className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-3.5 font-semibold transition-colors"
    >
      <Phone className="h-4 w-4" /> Need it faster? Call {PHONE_DISPLAY}
    </a>
  </div>
);

export const ContactForm = ({ prefillService }) => {
  const [form, setForm] = useState({
    ...initialForm,
    service_needed: prefillService && SERVICES.some((s) => s.name === prefillService) ? prefillService : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm(form)) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!termsChecked) {
      toast.error("Please accept the Terms of Service and Repair Authorization to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, terms_accepted: true });
      setSubmitted(true);
      toast.success("Request received! We'll contact you shortly.");
    } catch {
      toast.error("Something went wrong. Please call us at " + PHONE_DISPLAY);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessCard firstName={form.name.split(" ")[0]} />;
  }

  return (
    <form onSubmit={handleSubmit} className="bg-[#121212] border border-zinc-800 rounded-xl p-6 sm:p-10 space-y-6" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-zinc-300">Name *</Label>
          <Input data-testid="contact-input-name" className={inputCls} placeholder="Your full name" value={form.name} onChange={set("name")} />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Phone Number *</Label>
          <Input data-testid="contact-input-phone" type="tel" className={inputCls} placeholder="(702) 555-0123" value={form.phone} onChange={set("phone")} />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">Email <span className="text-zinc-600">(optional)</span></Label>
        <Input data-testid="contact-input-email" type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={set("email")} />
      </div>

      <div className="grid grid-cols-3 gap-4 sm:gap-6">
        <div className="space-y-2">
          <Label className="text-zinc-300">Year *</Label>
          <Input data-testid="contact-input-year" className={inputCls} placeholder="2018" value={form.vehicle_year} onChange={set("vehicle_year")} />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Make *</Label>
          <Input data-testid="contact-input-make" className={inputCls} placeholder="Toyota" value={form.vehicle_make} onChange={set("vehicle_make")} />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Model *</Label>
          <Input data-testid="contact-input-model" className={inputCls} placeholder="Camry" value={form.vehicle_model} onChange={set("vehicle_model")} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-zinc-300">Service Needed *</Label>
          <Select value={form.service_needed} onValueChange={(v) => setForm({ ...form, service_needed: v })}>
            <SelectTrigger data-testid="contact-select-service" className={inputCls}>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-zinc-800 text-white max-h-72">
              {SERVICES.map((s) => (
                <SelectItem key={s.name} value={s.name} className="focus:bg-blue-600/20 focus:text-white">
                  {s.name}
                </SelectItem>
              ))}
              <SelectItem value="Not sure / Something else" className="focus:bg-blue-600/20 focus:text-white">
                Not sure / Something else
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Preferred Date</Label>
          <Input data-testid="contact-input-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.preferred_date} onChange={set("preferred_date")} />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">How did you hear about us? <span className="text-zinc-600">(optional)</span></Label>
        <Select value={form.referral_source} onValueChange={(v) => setForm({ ...form, referral_source: v })}>
          <SelectTrigger data-testid="contact-select-referral" className={inputCls}>
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-zinc-800 text-white">
            {REFERRAL_OPTIONS.map((o) => (
              <SelectItem key={o} value={o} className="focus:bg-blue-600/20 focus:text-white">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">Message</Label>
        <Textarea
          data-testid="contact-input-message"
          className={`${inputCls} h-28 py-3`}
          placeholder="Describe the problem — noises, warning lights, when it started..."
          value={form.message}
          onChange={set("message")}
        />
      </div>

      <div className="flex items-start gap-3 bg-[#0a0a0a] border border-zinc-800 rounded-sm p-4">
        <Checkbox
          id="terms-checkbox"
          data-testid="contact-terms-checkbox"
          checked={termsChecked}
          onCheckedChange={(v) => setTermsChecked(v === true)}
          className="mt-0.5 border-zinc-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
        />
        <label htmlFor="terms-checkbox" className="text-zinc-400 text-xs leading-relaxed cursor-pointer">
          I have read and agree to Mason's Mobile Mechanic's{" "}
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-terms-link"
            className="text-blue-400 underline underline-offset-2"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service and Repair Authorization
          </Link>
          . I authorize Mason's Mobile Mechanic to inspect, diagnose, and perform the requested services on my vehicle.
        </label>
      </div>

      <button
        data-testid="contact-submit-button"
        type="submit"
        disabled={submitting || !termsChecked}
        className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-8 py-4 font-semibold transition-colors"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitting ? "Sending..." : "Send Service Request"}
      </button>
    </form>
  );
};
